import { activeTopicIndex, followActiveTopic, prepareTopicSidebar, topicReadingLine } from './page-topics';

function slugifyHeading(text, index) {
	const slug = text
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9\s-]/g, "")
		.replace(/\s+/g, "-")
		.replace(/-+/g, "-");
	return slug || `section-${index + 1}`;
}

export function mountArticleToc(root = document) {
	root.querySelectorAll("[data-article-toc]").forEach((toc) => {
		const article = root.querySelector(toc.dataset.articleToc || "[data-article-content]");
		const list = toc.querySelector("[data-article-toc-list]");
		if (!article || !list) return;
		prepareTopicSidebar(toc);

		const headings = [...article.querySelectorAll("h1, h2, h3")];
		const links = [];
		const used = new Set();
		headings.forEach((heading, index) => {
			let id = heading.id || slugifyHeading(heading.textContent, index);
			const base = id;
			let suffix = 2;
			while (used.has(id) || (document.getElementById(id) && document.getElementById(id) !== heading)) {
				id = `${base}-${suffix++}`;
			}
			used.add(id);
			heading.id = id;
			const item = document.createElement("li");
			item.className = `is-${heading.tagName.toLowerCase()}`;
			const link = document.createElement("a");
			link.href = `#${id}`;
			link.textContent = heading.textContent.trim();
			// Native anchors use the reading offset without Lenis's extra header offset.
			link.addEventListener("click", (event) => event.stopPropagation());
			links.push(link);
			item.append(link);
			list.append(item);
		});

		if (!headings.length) toc.hidden = true;
		else trackArticleSection(article, headings, links, toc);
	});

	root.querySelectorAll("[data-blog-search]").forEach((input) => {
		const items = [...root.querySelectorAll("[data-blog-item]")];
		input.addEventListener("input", () => {
			const query = input.value.trim().toLowerCase();
			items.forEach((item) => {
				item.hidden = Boolean(query) && !item.dataset.searchText.includes(query);
			});
		});
	});

	root.querySelectorAll("[data-content-search]").forEach((search) => {
		const input = search.querySelector("[data-content-search-input]");
		const form = search.querySelector("form");
		const scopes = [...root.querySelectorAll("[data-search-scope]")];
		if (!input || !scopes.length) return;

		const applySearch = (value) => {
			const query = value.trim().toLowerCase();
			input.value = value;
			scopes.forEach((scope) => {
				const items = [...scope.querySelectorAll("[data-search-item]")];
				let visible = 0;
				items.forEach((item) => {
					const matches = !query || item.dataset.searchText.includes(query);
					item.dataset.filterHidden = matches ? "false" : "true";
					item.hidden = !matches;
					if (matches) visible += 1;
				});
				scope.dispatchEvent(new CustomEvent("solufy:filter"));
				const empty = scope.querySelector("[data-search-empty]");
				if (empty) empty.hidden = visible !== 0;
			});
		};

		input.addEventListener("input", () => applySearch(input.value));
		form?.addEventListener("submit", (event) => {
			event.preventDefault();
			applySearch(input.value);
			scopes[0].scrollIntoView({ behavior: "smooth", block: "start" });
		});
		search.querySelectorAll("[data-search-chip]").forEach((chip) => {
			chip.addEventListener("click", () => {
				const wasSelected = chip.getAttribute("aria-pressed") === "true";
				search.querySelectorAll("[data-search-chip]").forEach((item) => item.setAttribute("aria-pressed", "false"));
				chip.setAttribute("aria-pressed", String(!wasSelected));
				applySearch(wasSelected ? "" : (chip.dataset.searchChip || chip.textContent));
				scopes[0].scrollIntoView({ behavior: "smooth", block: "start" });
			});
		});
	});
}

function trackArticleSection(article, headings, links, toc) {
	let scheduled = false;
	let activeIndex = -1;

	const update = () => {
		scheduled = false;
		const marker = topicReadingLine();
		const current = activeTopicIndex(headings, marker);
		followActiveTopic(toc, links[current]);
		if (current === activeIndex) return;
		activeIndex = current;
		links.forEach((link, index) => {
			if (index === current) link.setAttribute("aria-current", "location");
			else link.removeAttribute("aria-current");
		});
	};

	const schedule = () => {
		if (scheduled) return;
		scheduled = true;
		window.requestAnimationFrame(update);
	};

	window.addEventListener("scroll", schedule, { passive: true });
	window.addEventListener("resize", schedule);
	window.addEventListener("hashchange", schedule);
	window.addEventListener("load", schedule, { once: true });
	if ("ResizeObserver" in window) new ResizeObserver(schedule).observe(article);
	update();
}
